import { createContext, useContext, useState } from "react";
import { create } from "zustand";

const PerfManagerCtx = createContext<PerfManagerContext | null>(null);

const useMonitorStore = create<{ isMonitorActive: boolean }>(() => ({ isMonitorActive: true }));
export const setIsMonitorActive = (v: boolean) => useMonitorStore.setState({ isMonitorActive: v });

// Simplified enum with 4 states
export enum PerfState {
  POOR = 0,
  MEDIUM,
  GOOD,
  PERFECT,
}

type PerfManagerContext = {
  states: PerfState[];
  current: PerfState;
  autoUpdate: boolean;
  upgrade: () => void;
  degrade: () => void;
  setState: (s: PerfState) => void;
  setAutoUpdate: (autoUpdate: boolean) => void;
};

export const PERF_MAP = {
  [PerfState.POOR]: {
    dpr: 1,
    bottle: {
      samples: 2,
      anisotropy: 0,
      chromaticAberration: 0,
      temporalDistortion: 0,
      clearcoat: 0,
    },
  },
  [PerfState.MEDIUM]: {
    dpr: 1.25,
    bottle: {
      samples: 4,
      anisotropy: 0.02,
      chromaticAberration: 0.02,
      temporalDistortion: 0.1,
      clearcoat: 0.4,
    },
  },
  [PerfState.GOOD]: {
    dpr: 1.5,
    bottle: {
      samples: 8,
      anisotropy: 0.1,
      chromaticAberration: 0.04,
      temporalDistortion: 0.3,
      clearcoat: 0.7,
    },
  },
  [PerfState.PERFECT]: {
    dpr: 2,
    bottle: {
      samples: 16,
      anisotropy: 0.5,
      chromaticAberration: 0.06,
      temporalDistortion: 0.5,
      clearcoat: 1.0,
    },
  },
};

export function PerfManager({ children }: { children: React.ReactNode }) {
  const [autoUpdate, setAutoUpdate] = useState<boolean>(true);
  const [current, setCurrent] = useState(PerfState.PERFECT);
  const states = Object.values(PerfState).filter((v) => typeof v === "number") as PerfState[]; // prettier-ignore

  const clamp = (v: number) => Math.max(PerfState.POOR, Math.min(v, PerfState.PERFECT)); // prettier-ignore

  const upgrade = () => {
    if (current === PerfState.PERFECT || !autoUpdate || !useMonitorStore.getState().isMonitorActive) return;
    setCurrent((p) => {
      const next = clamp(p + 1);
      console.log(`increasing device quality, performance upgraded from ${PerfState[p]} to ${PerfState[next]}`); // prettier-ignore
      return next;
    });
  };

  const degrade = () => {
    if (current === PerfState.POOR || !autoUpdate || !useMonitorStore.getState().isMonitorActive) return;
    setCurrent((p) => {
      const next = clamp(p - 1);
      console.log(`decreasing device quality, performance degraded from ${PerfState[p]} to ${PerfState[next]}`); // prettier-ignore
      return next;
    });
  };

  const setState = (s: PerfState) => {
    const index = states.indexOf(s);
    if (index >= 0) setCurrent(index);
  };

  const value: PerfManagerContext = {
    states,
    current: states[current],
    upgrade,
    degrade,
    setState,
    autoUpdate,
    setAutoUpdate,
  };
  return (
    <PerfManagerCtx.Provider value={value}>{children}</PerfManagerCtx.Provider>
  );
}

export function usePerfManager() {
  const ctx = useContext(PerfManagerCtx);
  if (!ctx) throw new Error("usePerfManager must be used inside PerfManager");

  const { current } = ctx;

  return {
    settings: PERF_MAP[current],

    perfState: {
      isPoor: current === PerfState.POOR,
      isMedium: current === PerfState.MEDIUM,
      isGood: current === PerfState.GOOD,
      isPerfect: current === PerfState.PERFECT,
      current: current,
    },

    upgrade: ctx.upgrade,
    degrade: ctx.degrade,
    setState: ctx.setState, // set a specific state

    autoUpdate: ctx.autoUpdate,
    isMonitorActive: useMonitorStore.getState().isMonitorActive,
    setIsMonitorActive,
    states: ctx.states,
    setAutoUpdate: ctx.setAutoUpdate,
  };
}
